import { z } from "zod";

import { type RedlineTextProposal, redlineTextProposalSchema } from "@common/utils/redline/wire/redline-payload";

import { logWarning } from "./sentry-reporter";

const PROPOSAL_EDITS_STORAGE_KEY = "dioptra.proposalEdits.v2";

export const UNREADABLE_PROPOSAL_EDIT_MESSAGE = "A saved card edit could not be read and was left out";

const redlineEditSchema = z.object({
    kind: z.literal("redline"),
    textRedlineBeforeEdit: redlineTextProposalSchema,
    textRedline: redlineTextProposalSchema,
    textRedlineWhenOpened: redlineTextProposalSchema.nullable().default(null),
    editedAt: z.string(),
});

const commentEditSchema = z.object({
    kind: z.literal("comment"),
    textBeforeEdit: z.string(),
    text: z.string(),
    editedAt: z.string(),
});

export const proposalEditSchema = z.discriminatedUnion("kind", [redlineEditSchema, commentEditSchema]);

export type ProposalEdit = z.infer<typeof proposalEditSchema>;
export type RedlineProposalEdit = Extract<ProposalEdit, { kind: "redline" }>;
export type CommentProposalEdit = Extract<ProposalEdit, { kind: "comment" }>;

export interface RedlineProposalEditInput {
    kind: "redline";
    textRedlineBeforeEdit: RedlineTextProposal;
    textRedline: RedlineTextProposal;
    textRedlineWhenOpened: RedlineTextProposal | null;
}

export interface CommentProposalEditInput {
    kind: "comment";
    textBeforeEdit: string;
    text: string;
}

export type ProposalEditInput = RedlineProposalEditInput | CommentProposalEditInput;

export type ProposalEditsByAction = ReadonlyMap<string, ProposalEdit>;
export type ProposalEditsByConversation = ReadonlyMap<string, ProposalEditsByAction>;

const storedRecordSchema = z.record(z.string(), z.unknown());

type StoredRecord = z.infer<typeof storedRecordSchema>;

interface UnreadableStoredEdit {
    conversationId: string | null;
    actionId: string | null;
    storedEdit: unknown;
    unreadableFields: string;
}

interface StoredEditsReading {
    edits: ProposalEditsByConversation;
    unreadable: UnreadableStoredEdit[];
}

export const NO_PROPOSAL_EDITS: ProposalEditsByAction = new Map();

function unreadableFieldsOf(error: z.ZodError): string {
    return error.issues.map((issue) => issue.path.join(".") || "(the whole value)").join(", ");
}

function jsonOrTextOf(raw: string): unknown {
    try {
        return JSON.parse(raw);
    } catch {
        return raw;
    }
}

function readStoredEdits(storage: Storage): StoredEditsReading {
    const raw = storage.getItem(PROPOSAL_EDITS_STORAGE_KEY);
    if (raw === null) return { edits: new Map(), unreadable: [] };
    const stored = storedRecordSchema.safeParse(jsonOrTextOf(raw));
    if (!stored.success) return { edits: new Map(), unreadable: [{ conversationId: null, actionId: null, storedEdit: raw, unreadableFields: unreadableFieldsOf(stored.error) }] };
    const edits = new Map<string, ProposalEditsByAction>();
    const unreadable: UnreadableStoredEdit[] = [];
    for (const [conversationId, storedConversation] of Object.entries(stored.data)) {
        const storedActions = storedRecordSchema.safeParse(storedConversation);
        if (!storedActions.success) {
            unreadable.push({ conversationId, actionId: null, storedEdit: storedConversation, unreadableFields: unreadableFieldsOf(storedActions.error) });
            continue;
        }
        const editsByAction = new Map<string, ProposalEdit>();
        for (const [actionId, storedEdit] of Object.entries(storedActions.data)) {
            const edit = proposalEditSchema.safeParse(storedEdit);
            if (edit.success) editsByAction.set(actionId, edit.data);
            else unreadable.push({ conversationId, actionId, storedEdit, unreadableFields: unreadableFieldsOf(edit.error) });
        }
        if (editsByAction.size > 0) edits.set(conversationId, editsByAction);
    }
    return { edits, unreadable };
}

export function loadProposalEdits(storage: Storage | null): ProposalEditsByConversation {
    if (storage === null) return new Map();
    try {
        const reading = readStoredEdits(storage);
        for (const entry of reading.unreadable) {
            logWarning(UNREADABLE_PROPOSAL_EDIT_MESSAGE, { conversation_id: entry.conversationId, action_id: entry.actionId, unreadable_fields: entry.unreadableFields });
        }
        return reading.edits;
    } catch {
        return new Map();
    }
}

function unreadableEditsToKeep(storage: Storage, edits: ProposalEditsByConversation): Map<string, StoredRecord> {
    const kept = new Map<string, StoredRecord>();
    for (const entry of readStoredEdits(storage).unreadable) {
        if (entry.conversationId === null || entry.actionId === null) continue;
        if (edits.get(entry.conversationId)?.has(entry.actionId)) continue;
        kept.set(entry.conversationId, { ...kept.get(entry.conversationId), [entry.actionId]: entry.storedEdit });
    }
    return kept;
}

export function saveProposalEdits(storage: Storage | null, edits: ProposalEditsByConversation): void {
    if (storage === null) return;
    try {
        const plain: Record<string, StoredRecord> = Object.fromEntries(unreadableEditsToKeep(storage, edits));
        for (const [conversationId, editsByAction] of edits) plain[conversationId] = { ...plain[conversationId], ...Object.fromEntries(editsByAction) };
        storage.setItem(PROPOSAL_EDITS_STORAGE_KEY, JSON.stringify(plain));
    } catch {
        return;
    }
}

export function browserStorage(): Storage | null {
    try {
        return typeof window === "undefined" ? null : window.localStorage;
    } catch {
        return null;
    }
}
